import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  addDirectEmissions41_1cLCA,
  deleteDirectEmissions41_1cLCA,
  getAllDirectEmissions41_1cLCA,
  updateDirectEmissions41_1cLCA,
} from "../../../../../../apis/Product/UseStageFlow41Api";
import { getProductDirectEmission41_1cDropdown } from "../../../../../../apis/Dropdown";
import { REFRESH_USE_STAGE_41_1_TOTALS } from "../../Header";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const DirectEmissions41_1cTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [directEmissionDropdown, setDirectEmissionDropdown] = useState([]);
  const [unitDirectEmissions, setUnitDirectEmissions] = useState({});

  // Fetch direct emission dropdown (Flow 41.1c)
  useEffect(() => {
    getProductDirectEmission41_1cDropdown({
      setDropdown: setDirectEmissionDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllDirectEmissions41_1cLCA({
      productId,
      setObjects: (rows) => {
        setData(rows);
        window.dispatchEvent(new Event(REFRESH_USE_STAGE_41_1_TOTALS));
      },
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && directEmissionDropdown.length > 0) {
      const newUnits = {};
      data.forEach((row) => {
        const emissionCode =
          typeof row.direct_emission === "string"
            ? row.direct_emission
            : row.direct_emission?.value || row.direct_emission?.code;
        if (emissionCode) {
          const selected = directEmissionDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === emissionCode || opt.value === emissionCode)
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
          }
        }
      });
      setUnitDirectEmissions(newUnits);
    }
  }, [data, directEmissionDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      direct_emission: {
        targetField: "quantity_per_single_use",
        getPlaceholder: (selectedValue, dropdownData, _rowData, rowId = "new") => {
          const selected = dropdownData?.find(
            (opt) => opt?.value === selectedValue || opt?.code === selectedValue
          );
          if (selected) {
            setUnitDirectEmissions((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "direct_emission",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const emissionCode =
        typeof editedItem.direct_emission === "string"
          ? editedItem.direct_emission
          : editedItem.direct_emission?.value || editedItem.direct_emission?.code || null;
      updateDirectEmissions41_1cLCA({
        productId,
        rowId: editedItem.id,
        process_name: editedItem.process_name || null,
        direct_emission: emissionCode,
        quantity_per_single_use: editedItem.quantity_per_single_use || 0,
        cycles_per_lifetime: editedItem.cycles_per_lifetime || 0,
        objects: data,
        setObjects: setData,
        use_swal: true,
        callback: () =>
          window.dispatchEvent(new Event(REFRESH_USE_STAGE_41_1_TOTALS)),
      });
    },
    [productId, data]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const emissionCode =
        typeof newItem.direct_emission === "string"
          ? newItem.direct_emission
          : newItem.direct_emission?.value || newItem.direct_emission?.code || null;
      addDirectEmissions41_1cLCA({
        productId,
        process_name: newItem.process_name || null,
        direct_emission: emissionCode,
        quantity_per_single_use: newItem.quantity_per_single_use || 0,
        cycles_per_lifetime: newItem.cycles_per_lifetime || 0,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteDirectEmissions41_1cLCA({
        productId,
        rowId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "process_name",
        accessor: "process_name",
        header: `${t("PROCESS_NAME") || "Process name"} *`,
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: "direct_emission",
        accessor: "direct_emission",
        header: `${t("DIRECT_EMISSION") || "Direct emission"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_DIRECT_EMISSION") || "Select direct emission...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "direct_emission",
            directEmissionDropdown,
            unitDirectEmissions,
            rowId
          ),
      },
      {
        id: "quantity_per_single_use",
        accessor: "quantity_per_single_use",
        header: `${t("QUANTITY_PER_SINGLE_USE") || "Quantity per single use per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "direct_emission",
            directEmissionDropdown,
            unitDirectEmissions,
            rowId
          ),
      },
      {
        id: "cycles_per_lifetime",
        accessor: "cycles_per_lifetime",
        header: `${t("CYCLES_PER_LIFETIME") || "Cycles (Uses) per functional unit during lifetime"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitDirectEmissions,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      direct_emission: directEmissionDropdown || [],
    }),
    [directEmissionDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({ [impactCategoryAccessor]: CustomInputComponent }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default DirectEmissions41_1cTable;

